import { useTranslation } from 'react-i18next';
import { logEvent } from '@amplitude/analytics-browser';
import LibTable, {
  CustomTableProps as LibTableProps,
  PaginationSettings as LibPaginationSettings,
  isPageNumPaginationSettings,
} from '../../libs/frontend-lib/components/Table';

export * from '../../libs/frontend-lib/components/Table';

type PaginationSettings = LibPaginationSettings & {
  // Amplitude event logged on every page change.
  trackingEvent?: string;
};

export type CustomTableProps = Omit<LibTableProps, 'paginationSettings'> & {
  paginationSettings?: PaginationSettings | null;
};

// Bridge to the lib component: adds the translated paginator texts and the page change tracking,
// so the call sites do not change.
const Table = ({ paginationSettings, ...props }: CustomTableProps) => {
  const { t } = useTranslation('common');

  let libPaginationSettings: LibPaginationSettings | null = null;
  if (paginationSettings) {
    const { trackingEvent, ...settings } = paginationSettings;
    // The numbered paginator (showPages) always logged the 1-based page; the others the 0-based one.
    const pageOffset =
      isPageNumPaginationSettings(paginationSettings) && paginationSettings.showPages ? 1 : 0;
    libPaginationSettings = {
      ...settings,
      handlePageChange: (event: any, newPage: number) => {
        settings.handlePageChange(event, newPage);
        trackingEvent && logEvent(trackingEvent, { newPage: newPage + pageOffset });
      },
    } as LibPaginationSettings;
  }

  return (
    <LibTable
      paginationLabels={{
        rowsPerPage: t('common:paginator.rowsPerPage'),
        ofAmount: t('common:paginator.ofAmount'),
      }}
      {...props}
      paginationSettings={libPaginationSettings}
    />
  );
};

export default Table;
